import { useEffect, useRef, useState } from "react";
import {
  defaultAdjustments,
  damPageSchema,
  damSelectionSchema,
  type DamFormServices,
  type Field,
  type DamPage,
  type DamBrowseInput,
  type Values,
} from "@proof/core";

export function DamPicker({
  field,
  services,
  disabled,
  scope,
  onChange,
}: {
  field: Field;
  services?: DamFormServices;
  disabled: boolean;
  scope: string;
  onChange: (value: Values[string]) => void;
}) {
  const [page, setPage] = useState<DamPage>(),
    [query, setQuery] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = useRef<AbortController>(undefined),
    live = useRef({ field, services, disabled, scope, onChange });
  live.current = { field, services, disabled, scope, onChange };
  useEffect(() => {
    pending.current?.abort();
    pending.current = undefined;
    setPage(undefined);
    setQuery("");
    setError("");
    setBusy(false);
    return () => pending.current?.abort();
  }, [scope, services, disabled]);
  const run = async (
    input: DamBrowseInput,
    item?: { id: string; revision: string },
  ) => {
    const start = live.current;
    if (start.disabled || !start.services) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError("");
    const current = () =>
      !controller.signal.aborted &&
      pending.current === controller &&
      live.current.scope === start.scope &&
      live.current.services === start.services &&
      !live.current.disabled;
    try {
      if (item) {
        const generated = await start.services.select(
          start.field,
          {
            folderId: input.folderId || start.field.source.folderId!,
            itemId: item.id,
            revision: item.revision,
          },
          controller.signal,
        );
        const value = damSelectionSchema.parse(generated.value);
        if (current())
          live.current.onChange({
            ...value,
            adjustments: defaultAdjustments(),
          });
      } else {
        const found = damPageSchema.parse(
          await start.services.browse(start.field, input, controller.signal),
        );
        if (current()) setPage(found);
      }
    } catch {
      if (current()) setError("Image library request failed. Try again.");
    } finally {
      if (pending.current === controller && current()) {
        setBusy(false);
        pending.current = undefined;
      }
    }
  };
  const folderId = page?.path.at(-1)?.id || field.source.folderId;
  return (
    <fieldset disabled={disabled} className="dam-picker">
      <legend>{field.label} image library</legend>
      <p className="field-help">
        {services?.notice || "Browse images from the configured library."}
      </p>
      {!services ? (
        <p role="status">Image library service is unavailable.</p>
      ) : (
        <>
          {!page && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void run({ query: "" })}
            >
              Browse image library
            </button>
          )}
          {page && (
            <>
              <nav aria-label={`${field.label} library folders`}>
                {page.path.map((folder) => (
                  <button
                    type="button"
                    key={folder.id}
                    disabled={busy}
                    onClick={() => {
                      setQuery("");
                      void run({ folderId: folder.id, query: "" });
                    }}
                  >
                    {folder.name}
                  </button>
                ))}
              </nav>
              <label>
                Search image library
                <input
                  value={query}
                  maxLength={500}
                  disabled={busy}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (!busy) void run({ folderId, query });
                    }
                  }}
                />
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => void run({ folderId, query })}
              >
                Search library
              </button>
              <div>
                {page.folders.map((folder) => (
                  <button
                    type="button"
                    key={folder.id}
                    disabled={busy}
                    onClick={() => {
                      setQuery("");
                      void run({ folderId: folder.id, query: "" });
                    }}
                  >
                    Open {folder.name}
                  </button>
                ))}
              </div>
              <div>
                {page.items.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    disabled={busy}
                    onClick={() => void run({ folderId, query }, item)}
                  >
                    Use {item.name}
                  </button>
                ))}
              </div>
              {!page.items.length && !page.folders.length && (
                <p role="status">No library images found.</p>
              )}
              {page.nextCursor && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run({ folderId, query, cursor: page.nextCursor })
                  }
                >
                  Next library page
                </button>
              )}
            </>
          )}
        </>
      )}
      {busy && (
        <>
          <p role="status">Loading image library…</p>
          <button
            type="button"
            onClick={() => {
              pending.current?.abort();
              pending.current = undefined;
              setBusy(false);
            }}
          >
            Cancel library request
          </button>
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </fieldset>
  );
}
